import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { GroupsApi } from '@api/groups/groups.api';
import { Group } from '@core/interfaces';
import { chain, isEqual } from 'lodash';
import { merge, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'link-groups',
  templateUrl: './link-groups.component.html',
  styleUrls: ['../tab-groups.component.scss']
})
export class LinkGroupsComponent implements OnInit, OnDestroy {
  @ViewChild('form', { read: ElementRef, static: true })
  public form: ElementRef<HTMLDivElement>;

  public groupsToAdd: FormArray;
  public groupsPagedResult: any;
  public searchGroupsInput: FormControl;

  public filteredGroups: any[];
  public isLoadingGroups: boolean;
  public isAllSelected: boolean;
  public isAllLinked: boolean;
  public lastPaginatedGroupIndex: number;
  public firstPaginatedGroupIndex: number;

  private readonly destroyed$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public linkedGroups: Group[],
    private groupsApi: GroupsApi,
    private fb: FormBuilder
  ) {
    this.groupsPagedResult = {};
    this.isLoadingGroups = true;
    this.groupsToAdd = new FormArray([]);
    this.searchGroupsInput = new FormControl();
  }

  public async ngOnInit(): Promise<void> {
    await this.filterGroups(true);

    merge(this.searchGroupsInput.valueChanges)
      .pipe(debounceTime(500), takeUntil(this.destroyed$))
      .subscribe(() => this.filterGroups(true));
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public isLinkedGroup(groupId: string): boolean {
    return this.linkedGroups.map((group) => group.id).includes(groupId);
  }

  public isAddedGroup(groupId: string): boolean {
    return this.groupsToAdd.value.map((group) => group.id).includes(groupId);
  }

  public async selectOrDeselectAllGroups(select: boolean): Promise<void> {
    for (const group of this.groupsPagedResult.items) {
      const isLinked = this.isLinkedGroup(group.id);
      const isAdded = this.isAddedGroup(group.id);
      const isAddGroups = select && isAdded;
      const isRemoveGroups = !select && !isAdded;

      if (isLinked || isAddGroups || isRemoveGroups) {
        continue;
      }

      this.addOrRemoveGroup(group);
    }
  }

  public addOrRemoveGroup(group: Group): void {
    const filteredGroupIndex = this.groupsToAdd.value.findIndex((formGroup) => formGroup.id === group.id);
    if (filteredGroupIndex >= 0) {
      this.groupsToAdd.removeAt(filteredGroupIndex);
      this.setIsAllChecked();
      return;
    }

    this.groupsToAdd.setControl(this.groupsToAdd.value.length, this.fb.group(group));
    this.setIsAllChecked();
  }

  private setDefaultPaginate(): void {
    this.lastPaginatedGroupIndex = 5;
    this.firstPaginatedGroupIndex = 1;
  }

  private async filterGroups(resetPaginate?: boolean): Promise<void> {
    this.isLoadingGroups = true;

    if (resetPaginate) {
      this.setDefaultPaginate();
    }

    const payload = {
      query: {
        name_or_description_contains: this.searchGroupsInput.value
      },
      options: {
        offset: this.firstPaginatedGroupIndex - 1,
        limit: 5,
        orderColumn: 'name',
        order: 'asc'
      }
    };

    this.groupsPagedResult = await this.groupsApi.search(payload.query, payload.options).toPromise();
    this.isLoadingGroups = false;
    this.setIsAllChecked();

    this.form.nativeElement.scrollTo({ top: 0 });
  }

  public async changePage(next?: boolean): Promise<void> {
    if (next) {
      this.lastPaginatedGroupIndex += 5;
      this.firstPaginatedGroupIndex += 5;
    } else {
      this.lastPaginatedGroupIndex -= 5;
      this.firstPaginatedGroupIndex -= 5;
    }

    await this.filterGroups();
  }

  private setIsAllChecked(): void {
    const unlinkedGroups = chain(this.groupsPagedResult.items)
      .filter((group) => !this.isLinkedGroup(group.id))
      .sortBy('name')
      .value();

    const linkedGroups = chain(this.groupsPagedResult.items)
      .filter((group) => this.isLinkedGroup(group.id))
      .sortBy('name')
      .value()
      .length;

    const addedGroups = chain(this.groupsPagedResult.items)
      .filter((group) => this.isAddedGroup(group.id))
      .sortBy('name')
      .value();

    this.isAllLinked = linkedGroups === 5 || linkedGroups === this.groupsPagedResult.totalCount;
    this.isAllSelected = !this.isAllLinked && isEqual(unlinkedGroups, addedGroups);
  }
}
